import React, { createContext, useContext, useMemo, useState, ReactNode } from "react";
import { Toast } from "components/Toast";
import { ToastType } from "../../components/Toast/types";

interface ToastProviderProps {
  children: ReactNode;
}

interface ToastContextProps {
  addToast: (type: ToastType, text: string, options: ToastOptions) => void;
  addSuccessToast: (text: string, options?: ToastOptions) => void;
  addFailureToast: (text: string, options?: ToastOptions) => void;
  activeToastId: string | null;
}

interface ToastOptions {
  duration?: number;
  showCloseIcon?: boolean;
  actionHandler?: () => void;
  actionText?: string;
}

const ToastContext = createContext<ToastContextProps | undefined>(undefined);

export const ToastProvider = ({ children }: ToastProviderProps) => {
  const [toastConfig, setToastConfig] = useState<ToastOptions & { message: string; id: string; type?: ToastType } | null>(null);

  const addToast = (type: ToastType, text: string, options: ToastOptions = {}) => {
    const toastId = `toast-${Date.now()}`;
    setToastConfig({ message: text, id: toastId, type, ...options });

    if (!options.actionHandler) {
      setTimeout(() => {
        setToastConfig((currentConfig) => (currentConfig?.id === toastId ? null : currentConfig));
      }, options.duration || 5000);
    }
  };

  const addSuccessToast = (text: string, options: ToastOptions = {} ) => addToast(ToastType.SUCCESS, text, options);

  const addFailureToast = (text: string, options: ToastOptions = {}) => addToast(ToastType.FAILURE, text, options);

  const handleClose = () => {
    setToastConfig(null);
  };

  const contextValue = useMemo(
    () => ({
      addToast,
      addSuccessToast,
      addFailureToast,
      activeToastId: toastConfig?.id || null,
    }),
    [toastConfig]
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {toastConfig && (
        <Toast
          type={toastConfig.type}
          message={toastConfig.message}
          duration={toastConfig.duration}
          showCloseIcon={toastConfig.showCloseIcon}
          onClose={handleClose}
          actionHandler={toastConfig.actionHandler}
          actionText={toastConfig.actionText}
        />
      )}
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextProps => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};
